import React, { ComponentType, lazy, Suspense } from "react";
import { Redirect, Route, Switch } from "react-router-dom";

import { StandaloneCalendar } from "./calendar/standaloneCalendar";

// Loading indicator component with minHeight for better layout stability
const LoadingFallback = () => (
  <div
    className="flex items-center justify-center"
    style={{ minHeight: 550, height: "100%", width: "100%" }}
  >
    <div className="text-lg">Loading...</div>
  </div>
);

// Custom lazy loader that works with named exports
function lazyWithPreload<T extends ComponentType<P>, P = Record<string, never>>(
  factory: () => Promise<{ [key: string]: T }>,
  componentName: string
) {
  const LazyComponent = lazy(() =>
    factory().then((module) => ({ default: module[componentName] }))
  );

  return LazyComponent;
}

// Lazy load components by their export names
const Home = lazyWithPreload(() => import("./home"), "Home");
const News = lazyWithPreload(() => import("./news"), "News");
const Post = lazyWithPreload(() => import("./post"), "Post");
const Page = lazyWithPreload(() => import("./page"), "Page");
const Competitions = lazyWithPreload(
  () => import("./competitions"),
  "Competitions"
);
const Community = lazyWithPreload(() => import("./community"), "Community");
const League = lazyWithPreload(() => import("./leagues"), "League");
const Login = lazyWithPreload(() => import("./auth"), "Login");
const Register = lazyWithPreload(() => import("./auth"), "Register");
const ResetPassword = lazyWithPreload(() => import("./auth"), "ResetPassword");
const ForgotUsername = lazyWithPreload(
  () => import("./auth"),
  "ForgotUsername"
);
const Dashboard = lazyWithPreload(() => import("./dashboard"), "Dashboard");
const Team = lazyWithPreload(() => import("./team"), "Team");
const Matches = lazyWithPreload(() => import("./matches"), "Matches");
const Tournament = lazyWithPreload(() => import("./tournaments"), "Tournament");
const UserProfile = lazyWithPreload(() => import("./profile"), "UserProfile");
const Shop = lazyWithPreload(() => import("./shop"), "Shop");
const PlayerRanking = lazyWithPreload(
  () => import("./playerRanking"),
  "PlayerRanking"
);

const TeamRanking = lazyWithPreload(
  () => import("./teamRanking"),
  "TeamRanking"
);
const HallOfFame = lazyWithPreload(() => import("./hallOfFame"), "HallOfFame");
const Social = lazyWithPreload(() => import("./social"), "Social");
const NotificationsPage = lazyWithPreload(
  () => import("./notifications"),
  "NotificationsPage"
);
const SocialPostPage = lazyWithPreload(
  () => import("./socialPostPage/SocialPostPage"),
  "SocialPostPage"
);
const RecruitmentPostPage = lazyWithPreload(
  () => import("./recruitmentPostPage/RecruitmentPostPage"),
  "RecruitmentPostPage"
);
const EditRecruitmentPage = lazyWithPreload(
  () => import("./editRecruitmentPage/EditRecruitmentPage"),
  "EditRecruitmentPage"
);
const ContactPage = lazyWithPreload(
  () => import("./contactNew/ContactPage"),
  "ContactPage"
);
const TeamRegister = lazyWithPreload(
  () => import("./teamRegister"),
  "TeamRegister"
);
const AdminTeamApplications = lazyWithPreload(
  () => import("./adminTeamApplications"),
  "AdminTeamApplications"
);

export const Routes: React.FC = () => {
  return (
    <Suspense fallback={<LoadingFallback />}>
      <Switch>
        <Route exact path="/" component={Home} />
        <Route exact path="/news" component={News} />
        <Route exact path="/news/:postId" component={Post} />
        <Route exact path="/pages/:pageId" component={Page} />
        <Route exact path="/competitions" component={Competitions} />
        <Route path="/community/:communitySlug" component={Community} />
        <Route path="/league/:leagueSlug" component={League} />
        <Route path="/login" component={Login} />
        <Route path="/register" component={Register} />
        <Route path="/team/register" component={TeamRegister} />
        <Route path="/forgotPassword" component={ResetPassword} />
        <Route path="/resetPassword" component={ResetPassword} />
        <Route path="/forgotUsername" component={ForgotUsername} />
        <Route path="/dashboard" component={Dashboard} />
        <Route
          exact
          path="/team/calendar"
          render={() => <Redirect to="/calendar" />}
        />
        <Route
          path="/team/:teamSlug/calendar"
          render={() => <Redirect to="/calendar" />}
        />

        <Route path="/team/:teamSlug" component={Team} />
        <Route path="/match/:matchId" component={Matches} />
        <Route path="/tournament/:tournamentSlug" component={Tournament} />
        <Route path="/user/:username" component={UserProfile} />
        <Route path="/shop" component={Shop} />
        <Route path="/players" component={PlayerRanking} />
        <Route path="/teams" component={TeamRanking} />
        <Route path="/hallOfFame" component={HallOfFame} />
        <Route path="/feeds" component={Social} />
        <Route path="/notifications" component={NotificationsPage} />
        <Route path="/social-post/:postId" component={SocialPostPage} />
        <Route
          path="/recruitment-post/:postId"
          component={RecruitmentPostPage}
        />
        <Route
          path="/edit-recruitment-post/:postId"
          component={EditRecruitmentPage}
        />
        <Route exact path="/contact" component={ContactPage} />
        <Route exact path="/calendar" component={StandaloneCalendar} />
        <Route
          path="/admin/team-applications"
          component={AdminTeamApplications}
        />
      </Switch>
    </Suspense>
  );
};

export default Routes;
